import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from "rxjs/operators";
import {isNullOrUndefined} from "@qntm-code/utils";

import {format} from 'date-fns';
import {CalendarType} from '@app/shared/enums/calendar-type.enum';
import {Format} from '@app/shared/enums/format.enum';
import {CommonTransformationService} from '@app/shared/services/common-transformation.service';
import {Calendar} from "@app/shared/calendar/calendar";
import {QueryResult} from "@app/shared/classes/query-result";
import {IdNamePair} from "@app/shared/classes/id-name-pair";
import {SelectItem} from "primeng/api";
import {plainToClass} from "class-transformer";

@Injectable()
export class CalendarService {

  constructor(private http: HttpClient, private transform: CommonTransformationService) {
  }

  getCurrentPeriod(subtype: CalendarType): Observable<Calendar> {
    const url = `/calendar/current?subtype=${subtype}`;

    return this.http.get<Calendar>(url);
  }

  getFiscalYearForDate(date?: string): Observable<string> {
    let url = `/calendar/fiscal-year-from-date`;

    if (!date) {
      date = format(new Date(), Format.FnsDataserviceDate);
    }
    url += `?date=${date}`;

    return this.http.get<string>(url);
  }

  getCurrentFiscalYear(): Observable<number> {
    const url = `/calendar/current-fiscal-year`;
    return this.http.get<number>(url);
  }

  searchCalendar(subtype: CalendarType, periodClosed?: boolean, year?: string, title?: string): Observable<QueryResult> {
    let url = `/calendar/search?subtype=${subtype}`;

    if (!isNullOrUndefined(periodClosed)) {
      url += `&periodClose=${String(periodClosed)}`;
    }
    if (year) {
      url += `&year=${year}`;
    }
    if (title) {
      url += `&title=` + encodeURIComponent(title);
      // url += `&title=`+ "Period";
    }
    return this.http.get<QueryResult>(url);
  }

  getCurrentCalendar(subtype: CalendarType): Observable<Calendar> {
    const url = `/calendar/current?subtype=${subtype}`;
    return this.http.get<Calendar>(url);
  }

  searchComplianceCalendarAsDropDown(): Observable<SelectItem[]> {
    return this.searchCalendar(CalendarType.Compliance).pipe(map(res => {
      return this.transform.convertToDropDown("description", res.list, "id");
    }));
  }

  searchCalendarAsDropdown(subtype: CalendarType, periodClosed?: boolean): Observable<SelectItem[]> {
    return this.searchCalendar(subtype, periodClosed).pipe(map(res => {
      return this.transform.convertCalendarToPeriodDropdown(res.list);
    }));
  }

  getAccountingPeriod(accountingPeriodId: number): Observable<Calendar> {
    const url = `/calendar/accounting-periods/${accountingPeriodId}`;

    return this.http.get<Calendar>(url);
  }

  getAccountingPeriodAsDropdown(accountingPeriodId: number): Observable<SelectItem[]> {
    return this.getAccountingPeriod(accountingPeriodId).pipe(map(res => {
      return this.transform.convertCalendarToPeriodDropdown([res]);
    }));
  }

  getOpenAccountingPeriods(): Observable<QueryResult> {
    const url = `/calendar/open-accounting-periods`;

    return this.http.get<QueryResult>(url);
  }

  getOpenAccountingPeriodsAsDropdown(): Observable<SelectItem[]> {
    return this.getOpenAccountingPeriods().pipe(map(res => {
      return this.transform.convertCalendarToPeriodDropdown(res.list);
    }));
  }

  getInvoicingPeriodsByAccountingPeriod(accountingPeriodId: number): Observable<QueryResult> {
    const url = `/calendar/invoicing-periods/accounting-periods/${accountingPeriodId}`;

    return this.http.get<QueryResult>(url);
  }

  getInvoicingPeriodsByAccountingPeriodAsDropdown(accountingPeriodId: number): Observable<SelectItem[]> {
    return this.getInvoicingPeriodsByAccountingPeriod(accountingPeriodId).pipe(map(res => {
      return this.transform.convertCalendarToPeriodDropdown(res.list);
    }));
  }

  getCalendarAfterToday(subtype: CalendarType): Observable<QueryResult> {
    const url = `/calendar/today-on?subtype=${subtype}`;

    return this.http.get<QueryResult>(url);
  }

  getCalendarAfterTodayAsDropdown(subtype: CalendarType): Observable<SelectItem[]> {
    return this.getCalendarAfterToday(subtype).pipe(map(res => {
      return this.transform.convertCalendarToPeriodDropdown(res.list);
    }));
  }

  getCalendarByDate(subtype: CalendarType, localDate: string): Observable<Calendar> {
    const url = `/calendar/by-date?subtype=${subtype}&localDate=${localDate}`;

    return this.http.get<Calendar>(url);
  }

  getCalendarYears(subtype: CalendarType, tillToday?: boolean, laborOrExpense?: boolean): Observable<IdNamePair[]> {
    let url = `/calendar/years?subtype=${subtype}`;

    if (tillToday != null) {
      url += `&tillToday=${String(tillToday)}`;
    } else {
      url += `&tillToday=true`;
    }
    if (laborOrExpense != null) {
      url += `&laborOrExpense=${String(laborOrExpense)}`;
    }
    return this.http.get<IdNamePair[]>(url);
  }

  getCalendarYearsAsDropdown(subtype: CalendarType, sortDescending?: boolean, tillToday?: boolean, addCurrentFY?: boolean, addNextFY?: boolean, laborOrExpense?: boolean): Observable<SelectItem<string>[]> {
    return this.getCalendarYears(subtype, tillToday, laborOrExpense).pipe(map(res => {
      let calendars = plainToClass(IdNamePair, res);

      if (calendars.length == 0 && addCurrentFY) {
        this.getFiscalYearForDate().pipe(map(res => {
          return <SelectItem<string>[]>this.transform.convertArrayToDropDown([res]);
        }));
      } else if (addNextFY) {
        const maxYear = calendars.reduce((max, y) => +y.id > max ? +y.id : max, +calendars[0].id);
        const nextYear = maxYear + 1;

        calendars.push({id: nextYear.toString(), name: nextYear.toString()});
      }

      if (sortDescending) {
        let dropdown = <SelectItem<string>[]>this.transform.convertToDropDown("name", calendars, "id");
        return dropdown.sort((a, b) => a.value > b.value ? -1 : a.value < b.value ? 1 : 0);
      } else {
        return <SelectItem<string>[]>this.transform.convertToDropDown("name", calendars, "id");
      }
    }));
  }

  getCalendarsForYear(subtype: CalendarType, fiscalYear: string): Observable<QueryResult> {
    const url = `/calendar/calendars-for-year?subtype=${subtype}&year=${fiscalYear}`;
    return this.http.get<QueryResult>(url);
  }

  getCalendarsForYearAsDropdown(subtype: CalendarType, fiscalYear: string, sortDescending?: boolean): Observable<SelectItem[]> {
    return this.getCalendarsForYear(subtype, fiscalYear).pipe(map(res => {
      return this.transform.convertCalendarToPeriodDropdown(res.list, false, sortDescending);
    }));
  }

  searchAccountingPeriods(): Observable<QueryResult> {
    const url = "/calendar/accounting-periods/search?periodClosed=false&count=-1";

    return this.http.get<QueryResult>(url);
  }

  searchAccountingPeriodsAsDropdown(): Observable<SelectItem[]> {
    return this.searchAccountingPeriods().pipe(map(res => {
      return this.transform.convertCalendarToPeriodDropdown(res.list);
    }));
  }

  getLastOpenAccountingPeriod(): Observable<Calendar> {
    const url = "/calendar/last-open-accounting-period";
    return this.http.get<Calendar>(url);
  }

  getLastClosedAccountingPeriod(): Observable<Calendar> {
    const url = "/calendar/last-closed-accounting-period";
    return this.http.get<Calendar>(url);
  }

  getLastAccountingPeriodForYear(year: string): Observable<Calendar> {
    const url = `/calendar/last-accounting-period-for-year?year=${year}`;
    return this.http.get<Calendar>(url);
  }

  getCalendarsForOpenAccountingPeriods(subtype: CalendarType): Observable<QueryResult> {
    let url = `/calendar/search-for-open-periods?subtype=${subtype}`;
    return this.http.get<QueryResult>(url);
  }

  getFutureCalendars(subtype: CalendarType): Observable<QueryResult> {
    const url = `/calendar/future-calendars?subtype=${subtype}`;
    return this.http.get<QueryResult>(url);
  }

  getOrgGoLiveCalendar(subType: CalendarType): Observable<Calendar> {
    const url = `/calendar/go-live-calendar?subtype=${subType}`;
    return this.http.get<Calendar>(url);
  }

  getNumberOfPeriods(calendarType: CalendarType, year:string): Observable<number> {
    const url = `/calendar/number-of-periods?subtype=${calendarType}&year=${year}`;
    return this.http.get<number>(url);
  }
}

